import React, { MutableRefObject, ForwardedRef, forwardRef, useMemo, useRef } from 'react';
import JokeComponent, { JokeComponentProps } from './JokeComponent';
import VectorDisk from './VectorDisk';
import { Colors } from '../../../data/Colors';
import { Color, Group } from 'three';
import { useFrame } from '@react-three/fiber';
import useCustomFwdRef from '../../../hooks/useCustomFwdRef';

type Props = JokeComponentProps & {
  happiness: number,
  winner: number,
  label: string,
  showHappy?: boolean,
  showWinner?: boolean,
  arrowAngle?: number,
  pulse?: number,
  texts: any,
};

const c1 = new Color();
const c2 = new Color();

const DetailedSphere = forwardRef(({
  happiness,
  winner,
  showHappy,
  showWinner,
  label,
  arrowAngle,
  pulse,
  texts,
  ...props
}: Props, fwdRef?: ForwardedRef<Group>) =>
{
  const ref = useRef<Group>(null);
  const winnerLooserColor = useMemo(() =>
  {
    c1.set(Colors.WinnerColor);
    c2.set(Colors.LoserColor);
    c2.lerp(c1, winner * .5 + .5);
    return c2.getHex();
  }, [ winner ]);

  const emotionColor = useMemo(() =>
  {
    c1.set(Colors.HappyColor);
    c2.set(Colors.SadColor);
    c2.lerp(c1, happiness * .5 + .5);
    return c2.getHex();
  }, [ happiness ]);

  const time = useRef(0);
  useFrame((state, delta) => {
    if (pulse) {
      time.current += delta;

      const group = (ref as MutableRefObject<Group>)?.current;
      if (group) {
        const sc = 1.0 + Math.sin(time.current * 10.0) * pulse;
        group.scale.set(sc, sc ,sc);
      }
    }
  });

  useCustomFwdRef(ref, fwdRef);

  return <JokeComponent {...props} ref={ref}>
    {showWinner &&
        <VectorDisk thickness={.002 / props.sphereSize} color={winnerLooserColor} azimuth={-winner * Math.PI * .5}
          tilt={Math.PI * .25} label={winner > 0 ? texts.winner : texts.loser} arrowScale={1.5}/>}
    {showHappy &&
        <VectorDisk thickness={.002 / props.sphereSize} color={emotionColor} azimuth={happiness * Math.PI * .5}
          tilt={-Math.PI * .25} label={happiness > 0? texts.happy : texts.sad} outline={0.13 * (props.sphereSize || 0.5)} arrowScale={1.5} />}

    <VectorDisk thickness={.002 / props.sphereSize} color={props.color} azimuth={arrowAngle || 0}
      tilt={0} label={label} outline={0.13 * (props.sphereSize || 0.5)} arrowScale={1.5}/>
  </JokeComponent>;
});

DetailedSphere.displayName = 'DetailedSphere';

export default DetailedSphere;
